import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import { apiFetch, ApiClientError, setTokens } from '@/lib/api';
import { useAuthStore, type CurrentUser } from '@/stores/auth';
import { Button } from '@/components/Button';
import misesparkSymbol from '@/assets/logo/misespark-symbol-full-light.svg';

type LoginResponse = {
  access_token: string;
  refresh_token: string;
  user: CurrentUser;
};

// §7.7 ログイン: 温白 (surface.canvas) の地に L3 影でカードを浮かせる。
// 無効時のピンクは廃止 — opacity で示す (§7.1)。
export function LoginPage() {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 「エラー文言を出す」だけでは解決に進めない状態 (自力で抜けられない) は、
  // コードで分岐して脱出経路ごと出す。locked と suspended は排他なので1つの状態で持つ
  const [blocked, setBlocked] = useState<'locked' | 'suspended' | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  const queryClient = useQueryClient();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBlocked(null);
    setLoading(true);
    try {
      const data = await apiFetch<LoginResponse>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ identifier, password }),
      });
      setTokens(data.access_token, data.refresh_token);
      // 別テナントアカウントへの連続ログイン(ログアウト後の再ログイン等)で['nodes']等の
      // テナント非依存queryKeyの古いキャッシュ(最大staleTime分)が残り、ヘッダーの組織名等に
      // 前アカウントのテナント情報が表示され続ける不具合があったため、useSwitchTenant.tsと
      // 同様にログイン成功時点でキャッシュを破棄する
      await queryClient.cancelQueries();
      queryClient.clear();
      // ⚠️ §8-172系統: POST /auth/login のレスポンスは effective_permissions を含まない
      // (GET /auth/me だけが返す、apps/api/src/routes/auth.ts)。ここで data.user をそのまま
      // setUser すると、App.tsx のマウント時 /auth/me は既にこのタブで実行済み(トークン無し
      // だったため何もフェッチしていない)で以後リロードするまで二度と呼ばれず、
      // effective_permissions が undefined のまま (=permsReadyが恒久的にfalse) セッション中
      // ずっと固まってしまう。useSwitchTenant.ts と同じパターンで /auth/me を叩いて
      // 完全な CurrentUser を取得する (失敗時はログイン自体は成功しているため、
      // 不完全でも data.user へフォールバックしてブロックしない)。
      try {
        const me = await apiFetch<CurrentUser>('/auth/me');
        setUser(me);
      } catch {
        setUser(data.user);
      }
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.code === 'ACCOUNT_LOCKED') setBlocked('locked');
        if (err.code === 'ACCOUNT_SUSPENDED') setBlocked('suspended');
        setError(err.message);
      } else {
        setError('通信エラーが発生しました');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-canvas px-4">
      <div className="w-full max-w-md">
        <div className="bg-surface-card rounded-xl shadow-overlay p-8">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 justify-center mb-2">
              {/* フル版ロゴ (§1.2、48px以上で使用): 明背景用。皿の縁・スパーク・上昇リボンを含む構成 */}
              <img src={misesparkSymbol} alt="" className="w-12 h-12" />
            </div>
            <h1 className="text-[26px] font-extrabold tracking-tight">
              <span className="text-misespark-navy">Mise</span>
              <span className="bg-gradient-to-r from-misespark-orange to-misespark-yellow bg-clip-text text-transparent">Spark</span>
            </h1>
          </div>

          {error && !blocked && (
            <div
              role="alert"
              className="mb-4 p-3 rounded-md bg-danger-bg text-danger-text text-[13px] border border-danger-border flex items-start gap-2"
            >
              <AlertCircle size={16} className="shrink-0 mt-0.5" aria-hidden />
              <span>{error}</span>
            </div>
          )}

          {/* ロックは解除手順まで出さないと利用者が次に何をすればよいか分からない (本番申告)。
              解除経路は①LINEセルフリセット(引き換え時に自動解除、D-493) ②管理者による解除 の2つ。
              ⚠️ CTA を赤にしない — ログインボタンが既に赤で、赤は1画面1箇所 (docs/ui-guidelines.md §2.2)
              ⚠️ role="alert" にしない — WAI-ARIA は alert 内に操作要素を置くことを想定しておらず、
                 CTA ごと assertive で読み上げられる。読み上げ領域は文章だけに限り、リンクは外へ出す */}
          {blocked === 'locked' && (
            <div className="mb-4 p-3.5 rounded-md bg-danger-bg text-danger-text text-[13px] leading-[1.8] border border-danger-border">
              <div role="status" aria-live="polite">
                <div className="flex items-start gap-2">
                  <AlertCircle size={16} className="shrink-0 mt-1" aria-hidden />
                  <p className="text-[15px] font-extrabold">{error}</p>
                </div>
                <p className="mt-2">ログインするには、パスワードの再設定が必要です。</p>
                <ul className="mt-2 space-y-1.5 list-disc pl-5">
                  <li>
                    {/* ⚠️ 実フローは2段階 (D-499)。「パスワード」送信だけではリンクは届かず、
                        Botが返す「リンクを発行する」を押して初めて発行される (routes/line.ts)。
                        手順を1つ飛ばすと利用者はリンクを待ち続けることになる */}
                    <span className="font-bold">LINEと連携済みの方</span>：LINEのトーク画面で「パスワード」と送信し、
                    返ってきた<span className="font-bold">「リンクを発行する」</span>を押してください。
                    届いたリンクから再設定すると、ロックも解除されます。
                  </li>
                  <li>
                    <span className="font-bold">LINEと連携していない方</span>：店長または管理者にパスワードの再設定をご依頼ください。
                  </li>
                </ul>
              </div>
              <Link
                to="/forgot-password"
                className="mt-3 inline-flex items-center justify-center min-h-[44px] w-full px-4 rounded-md border border-chrome-base bg-surface-card text-chrome-base font-bold hover:bg-surface-sunken focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-chrome-base"
              >
                パスワードの再設定へ進む
              </Link>
            </div>
          )}

          {/* ⚠️ suspended はロックと違い**自力で復帰する手段が無い**。
              だからこそ「誰に言えばよいか」だけは必ず出す (これが無いと、誤操作で停止された人も
              退職者の再ログインも、同じ無案内メッセージの前で止まる)。
              ⚠️ パスワード再設定は案内しない — D-496 により suspended へはリセットトークンを
              発行しないため、案内すると必ず行き止まりになる */}
          {blocked === 'suspended' && (
            <div className="mb-4 p-3.5 rounded-md bg-danger-bg text-danger-text text-[13px] leading-[1.8] border border-danger-border">
              <div role="status" aria-live="polite">
                <div className="flex items-start gap-2">
                  <AlertCircle size={16} className="shrink-0 mt-1" aria-hidden />
                  <p className="text-[15px] font-extrabold">{error}</p>
                </div>
                <p className="mt-2">
                  このアカウントは現在ご利用いただけません。
                  <strong>ご利用の再開が必要な場合は、店長または管理者にお問い合わせください。</strong>
                </p>
                <p className="mt-2">パスワードを再設定しても、この状態は解除されません。</p>
              </div>
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label htmlFor="identifier" className="block text-[12px] font-bold text-text-sub mb-1.5">
                ログインIDまたはメールアドレス
              </label>
              <input
                id="identifier"
                type="text"
                required
                autoComplete="username"
                placeholder="例：yamada2026 または name@example.com"
                value={identifier}
                onChange={(e) => { setIdentifier(e.target.value); if (blocked) setBlocked(null); }}
                className="w-full h-[42px] px-3 rounded-md bg-surface-card text-text-main text-[13px] placeholder:text-text-muted shadow-[inset_0_0_0_1.5px_#E2DFDA] focus:outline-none focus:shadow-focus-ring transition-shadow"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-[12px] font-bold text-text-sub mb-1.5">
                パスワード
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="current-password"
                  minLength={8}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); if (blocked) setBlocked(null); }}
                  className="w-full h-[42px] px-3 pr-16 rounded-md bg-surface-card text-text-main text-[13px] placeholder:text-text-muted shadow-[inset_0_0_0_1.5px_#E2DFDA] focus:outline-none focus:shadow-focus-ring transition-shadow"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] font-bold text-text-sub hover:text-chrome-base px-1.5 py-1 rounded-md"
                  tabIndex={-1}
                >
                  {showPassword ? '非表示' : '表示'}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              variant="primary"
              size="md"
              className="w-full"
              disabled={!identifier || password.length < 8}
              loading={loading}
              loadingLabel="認証中…"
            >
              ログイン
            </Button>
          </form>

          {/* v0.4 追加要件⑲: セルフリセットの入口 */}
          <div className="mt-4 text-center">
            <Link
              to="/forgot-password"
              className="text-[12px] text-chrome-base underline underline-offset-2 hover:text-misespark-navy inline-flex items-center justify-center min-h-[44px] px-2"
            >
              パスワードをお忘れですか？
            </Link>
          </div>
        </div>
        <p className="text-center text-xs text-text-muted mt-4">
          © 2026 MiseSpark
        </p>
      </div>
    </div>
  );
}
